import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { authAPI, meliAPI } from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    country: 'MX'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isLogin) {
        const result = await authAPI.login(formData.email, formData.password);
        if (result.success) {
          localStorage.setItem('meli_user', JSON.stringify(result.user));
          localStorage.setItem('meli_token', result.token);
          toast({
            title: '¡Bienvenido!',
            description: 'Inicio de sesión exitoso',
          });
          navigate('/dashboard');
        }
      } else {
        const result = await authAPI.register(
          formData.name, 
          formData.email, 
          formData.password, 
          formData.country
        );
        if (result.success) {
          toast({
            title: '¡Registro exitoso!',
            description: 'Ahora puedes iniciar sesión',
          });
          setIsLogin(true);
          setFormData({ ...formData, password: '', name: '' });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Ocurrió un error. Intenta nuevamente.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMercadoLibreConnect = async () => {
    const user = localStorage.getItem('meli_user');
    if (!user) {
      toast({
        title: 'Debes iniciar sesión',
        description: 'Primero inicia sesión para conectar con Mercado Libre',
        variant: 'destructive'
      });
      return;
    }
    
    const userData = JSON.parse(user);
    const authUrl = await meliAPI.getAuthUrl(formData.country);
    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">MeLi Pro Analytics</h1>
          <p className="text-gray-400">Control total de tu rentabilidad</p>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 shadow-2xl">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 bg-gray-800/50 rounded-lg p-1">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 px-4 rounded-md transition-all duration-300 ${
                isLogin
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Iniciar Sesión
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 px-4 rounded-md transition-all duration-300 ${
                !isLogin
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Registrarse
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-gray-300 mb-2 block">
                  Nombre completo
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-indigo-500 transition-colors"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-gray-300 mb-2 block">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-indigo-500 transition-colors"
                required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-gray-300 mb-2 block">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-gray-800/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-indigo-500 transition-colors"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-6 transition-all duration-300 shadow-lg shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40"
            >
              {loading ? 'Cargando...' : (isLogin ? 'Iniciar Sesión' : 'Crear Cuenta')}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-900/80 text-gray-400">O continúa con</span>
            </div>
          </div>

          {/* Mercado Libre Connect */}
          <Button
            type="button"
            onClick={handleMercadoLibreConnect}
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold py-6 transition-all duration-300"
          >
            Conectar con Mercado Libre
          </Button>

          <p className="text-xs text-gray-500 mt-4 text-center">
            Al continuar, aceptas nuestros términos y condiciones
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
